"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getCurrentUTCDate = exports.getTimezoneFromState = exports.isSameDay = exports.isEndOfMonth = exports.effectiveBuildiumReversalDate = exports.isDatePostable = exports.calculateRenewalLeaseEndDate = exports.normalizeToDateString = exports.formatISODate = exports.TIME_ZONES = exports.DATE_FORMATS = void 0;
const date_fns_tz_1 = require("date-fns-tz");
const moment = require('moment-timezone');
const regions_1 = require("./regions");
exports.DATE_FORMATS = Object.freeze({
    READABLE_DATE: 'MMM Do, YYYY',
    YEAR_MONTH_DAY: 'YYYY-MM-DD'
});
exports.TIME_ZONES = Object.freeze({
    CENTRAL: 'America/Chicago',
    EASTERN: 'America/New_York',
    MOUNTAIN: 'America/Denver',
    PACIFIC: 'America/Los_Angeles',
    UTC: 'UTC'
});
const formatISODate = (date, format = exports.DATE_FORMATS.YEAR_MONTH_DAY) => {
    // This isMoment check is very important because it preserves
    // the timezones of any passed in dates that are already Moments
    return moment.isMoment(date) ? date.format(format) : moment(date).format(format);
};
exports.formatISODate = formatISODate;
const isValidCalendarDateString = (value) => {
    const date = new Date(`${value}T00:00:00.000Z`);
    return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
};
const DATE_STRING_REGEX = /^(\d{4}-\d{2}-\d{2})(?:$|T(?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d(?:\.\d{1,9})?)?(?:Z|[+-](?:[01]\d|2[0-3]):[0-5]\d)?)$/;
const normalizeToDateString = (value) => {
    var _a;
    if (value instanceof Date) {
        if (Number.isNaN(value.getTime())) {
            throw new Error('Invalid date');
        }
        const year = value.getUTCFullYear();
        const month = `${value.getUTCMonth() + 1}`.padStart(2, '0');
        const day = `${value.getUTCDate()}`.padStart(2, '0');
        return `${year}-${month}-${day}`;
    }
    if (typeof value === 'string') {
        const datePrefix = (_a = value.match(DATE_STRING_REGEX)) === null || _a === void 0 ? void 0 : _a[1];
        if (datePrefix) {
            if (!isValidCalendarDateString(datePrefix)) {
                throw new Error(`Invalid date: ${value}`);
            }
            return datePrefix;
        }
    }
    throw new Error(`Invalid date: ${value}`);
};
exports.normalizeToDateString = normalizeToDateString;
const calculateRenewalLeaseEndDate = ({ currentLeaseEndDate, originalLeaseStartDate, renewalTerm, isMonthToMonth = false }) => {
    if (!Number.isInteger(renewalTerm) || renewalTerm < 1) {
        throw new Error('renewalTerm must be an integer >= 1');
    }
    const currentLeaseEnd = moment(currentLeaseEndDate);
    const originalLeaseStart = moment(originalLeaseStartDate);
    if (!currentLeaseEnd.isValid() || !originalLeaseStart.isValid()) {
        throw new Error('Invalid renewal lease date inputs');
    }
    const renewalStartDate = currentLeaseEnd.clone().add(1, 'day');
    if (isMonthToMonth) {
        const startDay = renewalStartDate.date();
        const targetMonthStart = renewalStartDate.clone().date(1).add(renewalTerm, 'months');
        if (startDay > targetMonthStart.daysInMonth()) {
            return targetMonthStart.endOf('month').toDate();
        }
        return targetMonthStart.date(startDay).subtract(1, 'day').toDate();
    }
    const targetMonthDate = currentLeaseEnd.add(renewalTerm, 'months');
    const originalLeaseStartDay = originalLeaseStart.date();
    const getTargetEndDay = (date) => originalLeaseStartDay === 1
        ? date.daysInMonth()
        : Math.min(originalLeaseStartDay - 1, date.daysInMonth());
    const targetEndDate = targetMonthDate.date(getTargetEndDay(targetMonthDate));
    if (!targetEndDate.isAfter(renewalStartDate, 'day')) {
        targetEndDate.add(1, 'month').date(getTargetEndDay(targetEndDate));
    }
    return targetEndDate.toDate();
};
exports.calculateRenewalLeaseEndDate = calculateRenewalLeaseEndDate;
const isDatePostable = (date, lockDate) => {
    if (!lockDate) {
        return true;
    }
    return (0, exports.normalizeToDateString)(date) > (0, exports.normalizeToDateString)(lockDate);
};
exports.isDatePostable = isDatePostable;
const effectiveBuildiumReversalDate = ({ entryDate, chosenReversalDate, today }) => {
    if (chosenReversalDate) {
        return (0, exports.normalizeToDateString)(chosenReversalDate);
    }
    const normalizedEntryDate = (0, exports.normalizeToDateString)(entryDate);
    const normalizedToday = (0, exports.normalizeToDateString)(today);
    return normalizedEntryDate > normalizedToday ? normalizedEntryDate : normalizedToday;
};
exports.effectiveBuildiumReversalDate = effectiveBuildiumReversalDate;
const isEndOfMonth = (date) => {
    return moment(date).isSame(moment(date).endOf('month'), 'day');
};
exports.isEndOfMonth = isEndOfMonth;
const isSameDay = (date, secondDate) => {
    return moment(date).isSame(secondDate, 'day');
};
exports.isSameDay = isSameDay;
const getTimezoneFromState = (state) => {
    switch (state) {
        case regions_1.STATES.AL:
        case regions_1.STATES_FULL.AL:
        case regions_1.STATES.MO:
        case regions_1.STATES_FULL.MO:
            return exports.TIME_ZONES.CENTRAL;
        case regions_1.STATES.CA:
        case regions_1.STATES_FULL.CA:
            return exports.TIME_ZONES.PACIFIC;
        case regions_1.STATES.IN:
        case regions_1.STATES_FULL.IN:
        case regions_1.STATES.GA:
        case regions_1.STATES_FULL.GA:
        case regions_1.STATES.NC:
        case regions_1.STATES_FULL.NC:
            return exports.TIME_ZONES.EASTERN;
        default:
            throw new Error(`Cannot get timezone from state: ${state}.`);
    }
};
exports.getTimezoneFromState = getTimezoneFromState;
const getCurrentUTCDate = () => {
    // date-fns does not appear to have a great way to get the current date in
    // UTC regardless of what timezone this code is running in, so we're using moment
    // plus a hack to convert the date back into a native Date object. We should consider
    // replacing this with date-fns or another library in the future.
    return (0, date_fns_tz_1.toDate)(moment().utc().format(), { timeZone: 'UTC' });
};
exports.getCurrentUTCDate = getCurrentUTCDate;
